// src/App.jsx
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "react-hot-toast";
import { CartProvider } from "./context/CartContext";

import Navbar from "./components/Navbar";
import CartDrawer from "./components/CartDrawer/CartDrawer";
import Footer from "./components/Footer/Footer";
import AdminLayout from "./components/AdminLayout";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";
import Menu from "./pages/Menu";
import Cart from "./pages/Cart";
import Payment from "./pages/Payment";
import MyOrders from "./pages/MyOrders";
import UserProfile from "./pages/UserProfile";
import Register from "./pages/Register";
import Login from "./pages/Login";

import AdminLogin from "./pages/AdminLogin";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import Orders from "./pages/Orders";
import Categories from "./pages/Categories";
import Customers from "./pages/Customers";

import TableQRCodes from "./components/TableQRCodes";

import "./styles/App.css";
import "./styles/theme.css";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import FoodDetail from "./pages/FoodDetail";

// ─── TableContextLoader ────────────────────────────────────────────────────────
// Reads ?table=…&type=… from the URL once and stores in localStorage
function TableContextLoader({ children }) {
  let [params] = useSearchParams();
  useEffect(() => {
    const table = parseInt(params.get("table"), 10);
    const type  = params.get("type");
    if (!isNaN(table) && (type === "indoor" || type === "outdoor")) {
      localStorage.setItem(
        "tableInfo",
        JSON.stringify({ table, type })
      );
    }
  }, [params]);

  return children;
}

const AnimatedRoutes = ({ userData, setUserData, adminData, setAdminData, handleAdminLogout }) => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public User Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/menu" element={<Menu userData={userData} />} />
        <Route path="/cart" element={<Cart userId={userData?.id || null} />} />
        <Route path="/payment" element={<Payment userId={userData?.id || null} />} />
        <Route path="/orders" element={<MyOrders userId={userData?.id || null} />} />
        <Route path="/profile" element={<UserProfile userData={userData} setUserData={setUserData} />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login setUserData={setUserData} />} />

        {/* Admin login */}
        <Route path="/adminlogin" element={<AdminLogin setAdminData={setAdminData} />} />

        {/* Protected Admin Routes */}
        {adminData ? (
          <Route path="/admin" element={<AdminLayout handleAdminLogout={handleAdminLogout} />}>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="products" element={<Products />} />
            <Route path="orders" element={<Orders adminData={adminData} />} />
            <Route path="categories" element={<Categories />} />
            <Route path="customers" element={<Customers />} />
          </Route>
        ) : (
          <Route path="/admin/*" element={<Navigate to="/adminlogin" replace />} />
        )}

        {/* Catch‑all: redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/menu/:id" element={<FoodDetail />} />
        <Route path="/print-qrcodes" element={<TableQRCodes />} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  const [userData,  setUserData]  = useState(
    JSON.parse(localStorage.getItem("user"))  || null
  );
  const [adminData, setAdminData] = useState(
    JSON.parse(localStorage.getItem("admin")) || null
  );

  const handleUserLogout = () => {
    localStorage.removeItem("user");
    setUserData(null);
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("admin");
    setAdminData(null);
  };

  return (
    <CartProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <Navbar
        userData={userData}
        adminData={adminData}
        handleUserLogout={handleUserLogout}
        handleAdminLogout={handleAdminLogout}
      />
      <CartDrawer />

      <main>
        <TableContextLoader>
          <AnimatedRoutes 
            userData={userData} 
            setUserData={setUserData} 
            adminData={adminData} 
            setAdminData={setAdminData} 
            handleAdminLogout={handleAdminLogout} 
          />
        </TableContextLoader>
      </main>

      <Footer />
    </Router>
    </CartProvider>
  );
};

export default App;
